import { CompositeContext } from "./WJ37OVG2.js";
import { createElement, createHook, createMemoComponent } from "./NQJBHION.js";
import { useBooleanEvent, useEvent, useIsMouseMoving } from "./J7Q2EO23.js";
import { __objRest, __spreadProps, __spreadValues } from "./PNRLI7OV.js";

// src/composite/composite-hover.ts
import { useContext } from "react";
import { closest, contains } from "@ariakit/core/utils/dom";
import { hasFocus, hasFocusWithin } from "@ariakit/core/utils/focus";
import { invariant } from "@ariakit/core/utils/misc";
function getMouseDestination(event) {
  const relatedTarget = event.relatedTarget;
  if ((relatedTarget == null ? void 0 : relatedTarget.nodeType) === Node.ELEMENT_NODE) {
    return relatedTarget;
  }
  return null;
}
function hoveringInside(event) {
  const nextElement = getMouseDestination(event);
  if (!nextElement) return false;
  return contains(event.currentTarget, nextElement);
}
function movingToAnotherItem(event) {
  const dest = getMouseDestination(event);
  if (!dest) return false;
  const item = closest(dest, "[data-composite-hover]");
  return !!item;
}
var useCompositeHover = createHook(_a => {
  var _b = _a,
    {
      store,
      focusOnHover = true
    } = _b,
    props = __objRest(_b, ["store", "focusOnHover"]);
  const context = useContext(CompositeContext);
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "CompositeHover must be wrapped in a Composite component");
  const isMouseMoving = useIsMouseMoving();
  const onMouseMoveProp = props.onMouseMove;
  const focusOnHoverProp = useBooleanEvent(focusOnHover);
  const onMouseMove = useEvent(event => {
    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);
    if (event.defaultPrevented) return;
    if (!isMouseMoving()) return;
    if (!focusOnHoverProp(event)) return;
    if (!hasFocusWithin(event.currentTarget)) {
      const baseElement = store == null ? void 0 : store.getState().baseElement;
      if (baseElement && !hasFocus(baseElement)) {
        baseElement.focus();
      }
    }
    store == null ? void 0 : store.setActiveId(event.currentTarget.id);
  });
  const onMouseLeaveProp = props.onMouseLeave;
  const onMouseLeave = useEvent(event => {
    var _a2;
    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    if (event.defaultPrevented) return;
    if (!isMouseMoving()) return;
    if (hoveringInside(event)) return;
    if (movingToAnotherItem(event)) return;
    if (!focusOnHoverProp(event)) return;
    store == null ? void 0 : store.setActiveId(null);
    (_a2 = store == null ? void 0 : store.getState().baseElement) == null ? void 0 : _a2.focus();
  });
  props = __spreadProps(__spreadValues({
    "data-composite-hover": ""
  }, props), {
    onMouseMove,
    onMouseLeave
  });
  return props;
});
var CompositeHover = createMemoComponent(props => {
  const htmlProps = useCompositeHover(props);
  return createElement("div", htmlProps);
});
if (process.env.NODE_ENV !== "production") {
  CompositeHover.displayName = "CompositeHover";
}
export { useCompositeHover, CompositeHover };